// What the administrator's pages share: the words for states and risks, the group choice, and the icon picker.

import { ApiFailure, uploadIcon } from './api.ts';
import type { AdminIcon, AdminCategory, EntryKind, Risk, Status } from './types.ts';
import { esc, iconHtml, must, onAct, toast } from './util.ts';

export const STATUS_LABEL: Record<Status, string> = { scanning: '检测中', failed: '检测失败', pending: '待审核', rejected: '已拒绝', live: '已发布 · 公开中', down: '已发布 · 已下架', old: '历史版本' };
export const STATUS_CLASS: Record<Status, string> = { scanning: 'scanning', failed: 'failed', pending: 'pending', rejected: 'rejected', live: 'live', down: 'down', old: 'old' };
export const RISK_LABEL: Record<Risk, string> = { none: '未发现风险', low: '低风险', medium: '中风险', high: '高风险' };

export const statusChip = (status: Status): string =>
  `<span class="chip ${STATUS_CLASS[status]}">${status === 'scanning' ? '<span class="spin" style="width:9px;height:9px"></span>' : ''}${STATUS_LABEL[status]}</span>`;
export const riskHtml = (risk: Risk | null): string => (risk ? `<span class="risk ${risk}">${RISK_LABEL[risk]}</span>` : '<span class="dim">—</span>');

export const categorySelect = (id: string, categories: AdminCategory[], selected: string | null): string =>
  `<select id="${id}">${categories.map((category) => `<option value="${esc(category.id)}" ${category.id === selected ? 'selected' : ''}>${esc(category.name)}</option>`).join('')}
   <option value="" ${selected === null || !categories.some((category) => category.id === selected) ? 'selected' : ''}>未分类（显示在“其他”）</option></select>`;

// ---- icon picker ----

const PRESETS: [string, string][] = [['📊', '#1f5e45'], ['📄', '#2b4580'], ['📝', '#6b4520'], ['🔍', '#36367f'], ['🎨', '#7c2b65'], ['🛠️', '#454545'], ['✍️', '#5c3a1c'], ['🌐', '#1d5866'], ['🤖', '#52307d'], ['⚡', '#7a5e18'], ['🧪', '#25604f'], ['🗂️', '#555527']];

let picked: AdminIcon | null = null;
let pickedKind: EntryKind = 'skill';

export const pickedIcon = (): AdminIcon | null => picked;
/** The icon as the market's address takes it. */
export const iconPayload = (): { kind: 'preset'; glyph: string; color: string } | { kind: 'image'; key: string } | null =>
  picked === null ? null : picked.kind === 'preset' ? { kind: 'preset', glyph: picked.glyph, color: picked.color } : { kind: 'image', key: picked.key };

export function iconPickerHtml(current: AdminIcon | null, kind: EntryKind): string {
  picked = current;
  pickedKind = kind;
  const on = (glyph: string) => (picked?.kind === 'preset' && picked.glyph === glyph ? 'on' : '');
  return `<div class="field"><span>图标</span><div style="display:flex;gap:14px;align-items:center;margin-bottom:10px"><div id="icon-prev">${iconHtml(picked, kind, 'lg')}</div>
    <div class="dim">不设置时显示默认图标</div></div><div class="icon-picker" id="icon-grid">
    <button type="button" class="${picked ? '' : 'on'}" title="默认图标" data-act="icon-default">${iconHtml(null, kind, 'sm')}</button>
    ${PRESETS.map(([glyph, color]) => `<button type="button" class="${on(glyph)}" data-act="icon-preset" data-glyph="${esc(glyph)}" data-color="${esc(color)}">${iconHtml({ kind: 'preset', glyph, color }, kind, 'sm')}</button>`).join('')}
    <label class="btn btn-sm" style="height:36px;cursor:pointer">上传图片<input type="file" id="icon-file" accept="image/png,image/jpeg,image/webp,image/gif" hidden></label></div></div>`;
}

function showPicked(activeButton: HTMLElement | null): void {
  must('#icon-prev').innerHTML = iconHtml(picked, pickedKind, 'lg');
  document.querySelectorAll('#icon-grid button').forEach((button) => button.classList.toggle('on', button === activeButton));
}

onAct('icon-default', (element) => {
  picked = null;
  showPicked(element);
});
onAct('icon-preset', (element) => {
  picked = { kind: 'preset', glyph: element.dataset.glyph ?? '', color: element.dataset.color ?? '#454545' };
  showPicked(element);
});

/** A picture, cut to a square of 128 pixels and kept as PNG: all the pages show. */
function squareIcon(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = 128;
      const side = Math.min(image.width, image.height);
      canvas.getContext('2d')?.drawImage(image, (image.width - side) / 2, (image.height - side) / 2, side, side, 0, 0, 128, 128);
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('图片无法处理'))), 'image/png');
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('这张图片无法打开，请换一张。'));
    };
    image.src = url;
  });
}

document.addEventListener('change', async (event) => {
  const input = event.target as HTMLInputElement;
  if (input.id !== 'icon-file' || !input.files?.[0]) return;
  try {
    const { key } = await uploadIcon(await squareIcon(input.files[0]));
    picked = { kind: 'image', key, url: `/api/admin/${key}` };
    showPicked(null);
  } catch (thrown) {
    toast(thrown instanceof ApiFailure || thrown instanceof Error ? thrown.message : '图标上传失败，请稍后再试。');
  } finally {
    input.value = '';
  }
});
